import React, { useContext, useEffect, useState } from "react";
import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  Button,
  Checkbox,
  HStack,
  Select,
  Text,
  VStack,
} from "@chakra-ui/react";
import { MultiSelect } from "./MultiSelect";
import {
  DownloadableTablesEntry,
  Reportable,
  triggerMultiReportDownload,
} from "./ReportCreation";
import { FilterOutput, useReportFilter } from "./ReportFilter";
import { PromptForInputModal } from "../Modals/PromptForInputModal";
import { useReportData } from "../Services/ReportService";
import { Gender } from "../api/Dtos";
import { useGlobalContext } from "../Layout/GlobalState";

export enum ReportType {
  START_LIST = "Startlista",
  RESULT_LIST = "Resultatlista",
  START_NUMBER_LIST = "Nummerlappslista",
  NAME_LIST = "Namnlista",
}

const SelectableReportTypes: Array<ReportType> = [
  ReportType.START_LIST,
  ReportType.RESULT_LIST,
  ReportType.START_NUMBER_LIST,
  ReportType.NAME_LIST,
];

function reportTypeToString(type: ReportType): string {
  return type;
}

function stringToReportType(typeStr: string): ReportType {
  return (Object.values(ReportType) as string[]).includes(typeStr)
    ? (typeStr as ReportType)
    : SelectableReportTypes[0];
}

export function Reports() {
  const globalContext = useGlobalContext();

  const genders = [Gender.MALE, Gender.FEMALE];

  const [startgroups, setStartgroups] = useState<Array<number>>([]);

  const [filterReportType, setFilterReportType] = useState<ReportType>(
    SelectableReportTypes[0]
  );

  const [data, isDataLoaded] = useReportData(filterReportType);

  const {
    setClassFilter,
    setGenderFilter: setGenderFilter,
    setStartgroupFilter,
    filterOutput,
  } = useReportFilter(filterReportType, data);

  const [preliminary, setPreliminary] = useState<boolean>(false);

  useEffect(() => {
    setStartgroups([...new Set(data.map((d) => d.startgroup?.getTime() ?? 0))]);
  }, [data]);

  return (
    <VStack>
      <HStack
        padding={"10px"}
        spacing={4}
        justify={"space-evenly"}
        marginBottom={"30px"}
        align={"top"}
      >
        <MultiSelect
          title="Välj klass"
          values={
            globalContext.competition?.disciplines.map((c) => c.name) || []
          }
          displayValues={
            globalContext.competition?.disciplines.map((c) => c.name) || []
          }
          onChange={(
            selection: Array<string | number>,
            jointTable: boolean
          ) => {
            setClassFilter({
              filterFields: selection.map((e) => String(e)),
              jointTable: jointTable,
              filterParamName: "disciplineName",
            });
          }}
          defaultEmptyOption="Alla"
        />
        <MultiSelect
          title="Välj kön"
          values={genders}
          displayValues={genders}
          onChange={(
            selection: Array<string | number>,
            jointTable: boolean
          ) => {
            setGenderFilter({
              filterFields: selection.map((e) => String(e)),
              jointTable: jointTable,
              filterParamName: "gender",
            });
          }}
          defaultEmptyOption="Alla"
        />
        <MultiSelect
          title="Välj startgrupp"
          values={startgroups}
          displayValues={
            startgroups.map((t) => {
              const d = new Date(t);
              return `${d.getFullYear()}-${
                d.getMonth() + 1
              }-${d.getDate()} | ${String(d.getHours()).padStart(
                2,
                "0"
              )}:${String(d.getMinutes()).padStart(2, "0")}`;
            }) || []
          }
          onChange={(
            selection: Array<string | number>,
            jointTable: boolean
          ) => {
            setStartgroupFilter({
              filterFields: selection.map((e) => new Date(Number(e))),
              jointTable: jointTable,
              filterParamName: "startgroup",
            });
          }}
          defaultEmptyOption="Alla"
        />
        <VStack>
          <Text>Välj rapport</Text>
          <Select
            minW={100}
            placeholder=""
            onChange={(event) => {
              console.log(event);
              if (filterReportType != event.target.value) {
                setFilterReportType(stringToReportType(event.target.value));
              }
            }}
          >
            {SelectableReportTypes.map((r) => {
              return (
                <option key={r} value={r}>
                  {reportTypeToString(r)}
                </option>
              );
            })}
          </Select>
        </VStack>
        <VStack>
          <Text>Prel</Text>
          <Checkbox
            onChange={() => {
              setPreliminary((old) => !old);
            }}
            isChecked={preliminary}
          />
        </VStack>
      </HStack>
      <Text>Texten i rapportfältet sätts som filnamn och titel i rapporten</Text>
      <DownloadReportView
        reports={filterOutput}
        reportType={filterReportType}
        preliminary={preliminary}
      />
    </VStack>
  );
}

function DownloadReportView(props: {
  reports: FilterOutput<Reportable>;
  reportType: ReportType;
  preliminary: boolean;
}) {
  const [tableNames, setTableNames] = useState(
    props.reports.tables.map((r) => r.header)
  );

  const propsTableNames = props.reports.tables.map((t) => t.header);

  useEffect(() => {
    setTableNames(propsTableNames);
  }, [propsTableNames.join(":")]);

  const [requestDownloadAll, setRequestedDownloadAll] = useState(false);

  return (
    <VStack>
      {props.reports.tables.map((entry, index) => (
        <DownloadableTablesEntry
          key={entry.header}
          fileName={reportTypeToString(props.reportType)}
          tableLabel={tableNames[index]}
          data={entry.data}
          preliminary={props.preliminary}
          reportType={props.reportType}
          onChangeTableLabel={(newName: string) => {
            setTableNames((oldNames) => {
              return oldNames.map((oldName, i) => {
                return i === index ? newName : oldName;
              });
            });
          }}
        />
      ))}
      <Button
        onClick={() => {
          setRequestedDownloadAll(true);
        }}
      >
        Download all
      </Button>
      {requestDownloadAll && (
        <PromptForInputModal
          fileSuffix="pdf"
          suggestedInput={props.reportType}
          header="Spara alla rapporter i samma fil"
          request="Filnamn:"
          confirmButtonTitle="Ladda ner"
          abortButtonTitle="Avbryt"
          colorScheme="green"
          onConfirm={(filename: string) => {
            setRequestedDownloadAll(false);
            triggerMultiReportDownload(
              filename,
              tableNames,
              props.reports.tables.map((t) => t.data),
              props.reportType,
              props.preliminary ? "Preliminär" : undefined
            );
          }}
          onAbort={() => {
            setRequestedDownloadAll(false);
          }}
        />
      )}
    </VStack>
  );
}
